import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Card, Grid, CircularProgress,
  IconButton, Divider, Paper, Select, MenuItem, FormControl, InputLabel, Chip
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';
import RichTextEditor from '../../../components/common/RichTextEditor';

// --- 점수 평가 기준 ---
const scoreOptions = [
  { value: 0, label: '미평가 (Not Rated)' },
  { value: 1, label: '1점: 매우 미흡 (Unsatisfactory)' },
  { value: 2, label: '2점: 기대 이하 (Below Expectations)' },
  { value: 3, label: '3점: 보통 (Meets Expectations)' },
  { value: 4, label: '4점: 우수 (Exceeds Expectations)' },
  { value: 5, label: '5점: 매우 우수 (Outstanding)' },
];

// --- 동적 루브릭 빌더를 위한 헬퍼 함수 ---
const createNewCriterion = () => ({
  id: uuidv4(),
  title: '',
  description: '',
  score: 0,
  weight: 50, // 파트 내 기본 가중치
  comment: '',
});

const createNewSection = () => ({
  id: uuidv4(),
  title: '',
  weight: 50, // 전체 점수 내 파트 가중치
  criteria: [createNewCriterion()],
});

const MptEvaluationForm = ({ isSubmitting, onEvaluationSubmit }) => {
  const [mptTitle, setMptTitle] = useState('');
  const [rubricSections, setRubricSections] = useState([createNewSection()]);
  const [overallComment, setOverallComment] = useState('');
  const [totalScore, setTotalScore] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);

  // --- 상태 업데이트 핸들러 ---
  const handleSectionChange = (sectionId, field, value) => {
    setRubricSections(prevSections =>
      prevSections.map(section =>
        section.id === sectionId ? { ...section, [field]: value } : section
      )
    );
  };
  
  const handleCriterionChange = (sectionId, criterionId, field, value) => {
    setRubricSections(prevSections =>
      prevSections.map(section => {
        if (section.id === sectionId) {
          const updatedCriteria = section.criteria.map(criterion =>
            criterion.id === criterionId ? { ...criterion, [field]: value } : criterion
          );
          return { ...section, criteria: updatedCriteria };
        }
        return section;
      })
    );
  };

  const addSection = () => setRubricSections(prev => [...prev, createNewSection()]);
  const removeSection = (sectionId) => setRubricSections(prev => prev.filter(s => s.id !== sectionId));
  const addCriterion = (sectionId) => {
    setRubricSections(prev => prev.map(section =>
      section.id === sectionId ? { ...section, criteria: [...section.criteria, createNewCriterion()] } : section
    ));
  };
  const removeCriterion = (sectionId, criterionId) => {
    setRubricSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        return { ...section, criteria: section.criteria.filter(c => c.id !== criterionId) };
      }
      return section;
    }));
  };

  // --- 점수 계산 로직 (계층적 가중치 반영) ---
  const calculateScore = useCallback(() => {
    let finalWeightedScore = 0;
    let sectionWeightSum = 0;

    rubricSections.forEach(section => {
      const sectionWeight = Number(section.weight) || 0;
      let criteriaWeightedScoreSum = 0;
      let criteriaWeightSum = 0;
      
      section.criteria.forEach(criterion => {
        const score = Number(criterion.score) || 0;
        const weight = Number(criterion.weight) || 0;
        
        // 기준 점수(0~1 스케일)에 기준 가중치를 곱해 합산
        criteriaWeightedScoreSum += (score / 5) * weight;
        criteriaWeightSum += weight;
      });

      // 파트의 점수(0~1 스케일) 계산
      const sectionScore = criteriaWeightSum > 0 ? criteriaWeightedScoreSum / criteriaWeightSum : 0;

      // 파트 점수에 파트 가중치를 곱해 최종 점수에 합산
      finalWeightedScore += sectionScore * sectionWeight;
      sectionWeightSum += sectionWeight;
    });

    setTotalWeight(sectionWeightSum);
    
    const finalScore = sectionWeightSum > 0 ? (finalWeightedScore / sectionWeightSum) * 6 : 0;
    setTotalScore(finalScore.toFixed(2));
  }, [rubricSections]);

  useEffect(() => {
    calculateScore();
  }, [calculateScore]);

  // --- 제출 데이터 준비 ---
  const prepareSubmitData = (e) => {
    e.preventDefault();
    const submitData = {
      score: totalScore,
      evaluation_details: JSON.stringify({ mptTitle, rubricSections }),
      overall_comment: overallComment,
    };
    onEvaluationSubmit(submitData);
  };

  return (
    <Box component="form" onSubmit={prepareSubmitData}>
      {/* --- 헤더: MPT 제목 및 총점 --- */}
      <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="MPT 과제 제목 (e.g., Ashton v. Indigo Construction Co.)"
              variant="standard"
              value={mptTitle}
              onChange={(e) => setMptTitle(e.target.value)}
              sx={{ input: { fontSize: '1.25rem', fontWeight: 'bold' } }}
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ textAlign: { sm: 'right' } }}>
            <Chip 
              label={`파트 가중치 합계: ${totalWeight}%`}
              color={totalWeight === 100 ? 'success' : 'error'} 
              variant={totalWeight === 100 ? "filled" : "outlined"}
              sx={{ mr: 2, mb: { xs: 1, sm: 0 } }}
            />
            <Typography variant="h5" component="span">종합: {totalScore} / 6.00</Typography>
          </Grid>
        </Grid>
      </Card>

      {/* --- 동적 루브릭 섹션 --- */}
      {rubricSections.map((section, sectionIndex) => {
        const criteriaWeightSum = section.criteria.reduce((sum, crit) => sum + (Number(crit.weight) || 0), 0);

        return (
        <Paper key={section.id} elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <TextField
              sx={{ flexGrow: 1, minWidth: '200px' }}
              label={`평가 파트 ${sectionIndex + 1} 제목`}
              variant="filled"
              value={section.title}
              onChange={(e) => handleSectionChange(section.id, 'title', e.target.value)}
              placeholder="e.g., Argument I: Tribal Court Jurisdiction"
            />
             <TextField
                sx={{ width: '130px' }}
                label="파트 가중치(%)"
                type="number"
                variant="standard"
                value={section.weight}
                onChange={(e) => handleSectionChange(section.id, 'weight', e.target.value)}
              />
            <Chip 
              label={`기준 합계: ${criteriaWeightSum}%`}
              color={criteriaWeightSum === 100 ? 'success' : 'error'} 
              variant="outlined"
            />
            <IconButton onClick={() => removeSection(section.id)} color="error" aria-label="섹션 삭제">
              <DeleteIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {section.criteria.map((criterion, criterionIndex) => (
            <Box key={criterion.id} sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>{`세부 기준 ${criterionIndex + 1}`}</Typography>
                  <IconButton onClick={() => removeCriterion(section.id, criterion.id)} size="small" aria-label="세부 기준 삭제">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth label="기준 이름" variant="standard" placeholder="e.g., Consensual Relationship Exception" value={criterion.title} onChange={(e) => handleCriterionChange(section.id, criterion.id, 'title', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="핵심 포인트 (Point Sheet 내용)" variant="standard" placeholder="Point Sheet의 핵심 내용을 요약 또는 복사하세요..." value={criterion.description} onChange={(e) => handleCriterionChange(section.id, criterion.id, 'description', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
                    <InputLabel id={`score-select-label-${criterion.id}`}>점수</InputLabel>
                    <Select
                      labelId={`score-select-label-${criterion.id}`}
                      value={criterion.score}
                      onChange={(e) => handleCriterionChange(section.id, criterion.id, 'score', e.target.value)}
                    >
                      {scoreOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                   <TextField
                    fullWidth
                    label="기준 가중치(%)"
                    type="number"
                    variant="standard"
                    value={criterion.weight}
                    onChange={(e) => handleCriterionChange(section.id, criterion.id, 'weight', e.target.value)}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth multiline rows={2} label="코멘트" variant="standard" placeholder="이 기준에 대한 피드백을 작성하세요..." value={criterion.comment} onChange={(e) => handleCriterionChange(section.id, criterion.id, 'comment', e.target.value)} />
                </Grid>
              </Grid>
            </Box>
          ))}
          <Button startIcon={<AddCircleOutlineIcon />} onClick={() => addCriterion(section.id)} variant="text">
            세부 기준 추가
          </Button>
        </Paper>
        );
      })}
      
      <Button fullWidth variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addSection} sx={{ mb: 4, py: 1 }}>
        평가 파트 추가
      </Button>
      
      {/* --- 총평 --- */}
      <Card variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" component="h3" gutterBottom>최종 총평 및 조언</Typography>
        <RichTextEditor value={overallComment} onChange={setOverallComment} placeholder="종합적인 평가와 조언을 작성해주세요..." />
      </Card>
      
      {/* --- 제출 버튼 --- */}
      <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitting} sx={{ mt: 4, py: 1.5, fontWeight: 'bold' }}>
        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : '평가 완료 및 저장'}
      </Button>
    </Box>
  );
};

export default MptEvaluationForm; 